import React, { useEffect, useState } from "react";
import { DataTableComponent } from "../../../Components/DataTable";
import Icon from "../../../Components/icons";
import { Chip, EmptyScreen, Loader } from "../../../Components/Other";
import { PrimaryButton } from "../../../Components/Cta";
import { APIService } from "../../../Service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../../../Components/Modals";

export default function Users({ id }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [delId, setDelId] = useState(null);

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const COLUMNS = [
    {
      name: "Customer ID",
      selector: (row) => (row.customer_id ? `#${row.customer_id}` : "-"),
      sortable: true,
      sortFunction: (a, b) => (a.customer_id || 0) - (b.customer_id || 0),
      width: "160px",
    },
    {
      name: "Customer Name",
      selector: (row) => row.name || "-",
      sortable: true,
      width: "180px",
    },
    {
      name: "Joined On",
      selector: (row) => row.joined_on || "-",
      sortable: true,
      sortFunction: (a, b) =>
        new Date(a.joined_on?.split(" ").reverse().join(" ")) -
        new Date(b.joined_on?.split(" ").reverse().join(" ")),
      width: "170px",
    },
    {
      name: "Risk Appetite",
      selector: (row) => row.risk_appetite || "-",
      sortable: true,
      width: "170px",
      cell: (row) => {
        switch (row.risk_appetite) {
          case "Low Risk":
            return (
              <Chip
                label="Low Risk"
                borderColor="e-border-1px-green"
                bgColor="e-bg-pale-green"
                fontColor="e-text-green"
                fontWeight="e-font-weight-500"
              />
            );
          case "Moderate Risk":
            return (
              <Chip
                label="Moderate Risk"
                borderColor="e-border-1px-egyptian-blue"
                bgColor="e-bg-pale-egyptian-blue"
                fontColor="e-text-egyptian-blue"
                fontWeight="e-font-weight-500"
              />
            );
          case "High Risk":
            return (
              <Chip
                label="High Risk"
                fontWeight="e-font-weight-500"
                borderColor="e-border-1px-ruby"
                bgColor="e-bg-lavender-blush"
                fontColor="e-text-ruby"
              />
            );
          case "Very High Risk":
            return (
              <Chip
                label="Very High Risk"
                borderColor="e-border-1px-carmine-pink"
                bgColor="e-bg-misty-rose"
                fontColor="e-text-carmine-pink"
                fontWeight="e-font-weight-500"
              />
            );
          case "Very Low Risk":
            return (
              <Chip
                label="Very Low Risk"
                borderColor="e-border-1px-turquoise-green"
                bgColor="e-bg-baby-powder"
                fontColor="e-text-middle-green"
                fontWeight="e-font-weight-500"
              />
            );
          default:
            return <span>{row.risk_appetite}</span>;
        }
      },
    },
    {
      name: "AUM",
      selector: (row) => row.aum,
      sortable: true,
      cell: (row) => (
        <div className="e-secondary-font-Inter">
          ₹{(row.aum || 0).toLocaleString()}
        </div>
      ),
      sortFunction: (a, b) => (a.aum || 0) - (b.aum || 0),
      width: "140px",
    },
    {
      name: "Email",
      selector: (row) => row.email || "-",
      sortable: false,
      width: "220px",
    },
    {
      name: "Phone Number",
      selector: (row) => row.phone_number || "-",
      sortable: false,
      width: "160px",
    },
    {
      name: "KYC Status",
      selector: (row) => row.kyc_status || "-",
      width: "150px",
      sortable: false,
      cell: (row) => {
        switch (row.kyc_status?.toUpperCase()) {
          case "COMPLETED":
            return (
              <Chip
                label="Completed"
                borderColor="e-border-1px-green"
                bgColor="e-bg-pale-green"
                fontColor="e-text-green"
                fontWeight="e-font-weight-500"
              />
            );
          case "PENDING":
            return (
              <Chip
                label="Pending"
                borderColor="e-border-1px-independence"
                bgColor="e-bg-pale-independence"
                fontColor="e-text-independence"
                fontWeight="e-font-weight-500"
                fontSize="e-font-12"
              />
            );
          default:
            return <span>{row.kyc_status}</span>;
        }
      },
    },
    {
      name: "User Status",
      selector: (row) => row.user_status || "-",
      sortable: false,
      cell: (row) => {
        switch (row.user_status?.toUpperCase()) {
          case "ACTIVE":
            return (
              <Chip
                label="Active"
                borderColor="e-border-1px-green"
                bgColor="e-bg-pale-green"
                fontColor="e-text-green"
                fontWeight="e-font-weight-500"
              />
            );
          case "BLOCKED":
            return (
              <Chip
                label="Blocked"
                borderColor="e-border-1px-ruby"
                bgColor="e-bg-pale-ruby"
                fontColor="e-text-ruby"
                fontWeight="e-font-weight-500"
              />
            );
          default:
            return <span>{row.user_status}</span>;
        }
      },
    },
    {
      name: "Action",
      width: "350px",
      cell: (row) => (
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex justify-content-start align-items-center">
            <PrimaryButton
              name="View Details"
              onPress={() => {
                navigate(`/customer/detail/${row.customer_id}`);
              }}
              ClassName="bg-transparent e-text-blue-violet e-font-weight-400 e-font-14 px-0 py-2"
            />
            <Icon icon="arrow-right2" size="20px" className="ps-1" />
          </div>
          {row.user_status?.toUpperCase() === "BLOCKED" ? (
            <PrimaryButton
              name="Unblock User"
              icon="unblock"
              size="22px"
              onPress={() => {
                setDelId(row.customer_id);
              }}
              ClassName="bg-transparent e-text-gray e-font-weight-400 e-font-14"
            />
          ) : (
            <PrimaryButton
              name="Block User"
              icon="block"
              size="16px"
              onPress={() => {
                setDelId(row.customer_id);
              }}
              ClassName="bg-transparent e-text-cg-red e-font-weight-400 e-font-14"
            />
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    _getUsersList(currentPage, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentPage, rowsPerPage]);

  useEffect(() => {
    if (delId) {
      setShowModal(true);
    }
  }, [delId]);

  // service to get users list
  const _getUsersList = (page = 1, size = 10) => {
    setLoading(true);
    const is_post = true;
    const payload = {
      sub_admin_id: id,
      page_num: page,
      page_size: size,
    };

    APIService(is_post, "sub-admin/customer-list", payload)
      .then((response) => {
        const code = response?.status_code ?? response?.status;
        const raw = response?.data ?? response; // raw could be the object that contains customers or data

        // try multiple possible array locations
        const list = Array.isArray(raw?.customers)
          ? raw.customers
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw)
          ? raw
          : [];

        const total = raw?.total_count ?? raw?.total ?? list.length;
        const pageSize = raw?.page_size ?? raw?.pageSize ?? size;

        if (code === 200) {
          setUsers(list);
          setTotalRows(total ?? list.length);
          setRowsPerPage(pageSize ?? size);
          setIsEmpty(list.length === 0);
          return;
        }

        // If API resolves with non-200 (rare), treat as empty
        setUsers([]);
        setTotalRows(0);
        setRowsPerPage(size);
        setIsEmpty(true);
      })
      .catch((err) => {
        // Axios-style error -> err.response.status
        const status = err?.response?.status;

        if (status === 400 || status === 404) {
          // Explicit handling for "not found" / bad request
          setUsers([]);
          setTotalRows(0);
          setRowsPerPage(size);
          setIsEmpty(true);
          return;
        }

        // Generic error fallback
        console.error("API error:", err);
        setUsers([]);
        setTotalRows(0);
        setRowsPerPage(size);
        setIsEmpty(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // service to block/unblock a user
  const _blockUser = (id) => {
    const is_post = true;
    const data = {
      customer_id: id,
    };
    APIService(is_post, "user/block", data)
      .then((response) => {
        if (response.status_code === 200) {
          _getUsersList();
          setShowModal(false);
          setDelId(null);
          toast.success(response.message, {
            type: "success",
            className: "e-toast",
          });
        } else {
          console.error(response);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return loading ? (
    <div className="d-flex min-vh-100 justify-content-center align-items-center">
      <Loader width={30} />
    </div>
  ) : isEmpty ? (
    <EmptyScreen width={100} message={"No Users Found"} />
  ) : (
    <div className="row px-3 pt-3">
      <DataTableComponent
        columns={COLUMNS}
        data={users}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        currentPage={currentPage}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(newPerPage, page) => {
          setRowsPerPage(newPerPage);
          setCurrentPage(page || 1);
        }}
        sortIcon={<Icon icon="sort_col" size="20px" />}
        className="e-customer-investment-table"
      />

      {showModal && (
        <ConfirmationModal
          title={"Do You Want to Continue this Action"}
          message={"Do you want to proceed with your Action ?"}
          onClick={() => {
            _blockUser(delId);
          }}
          close={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
